/**
 * AppICEMAP — Geographic scope
 *
 * The app covers the United States only, so this is the single place that
 * decides what "inside the country" means: the map cannot be panned outside it,
 * address search is limited to it, and a report cannot be placed outside it.
 *
 * NOTE: this is a client-side boundary. It shapes the app's behaviour, it does
 * not enforce anything — a crafted request could still post coordinates from
 * anywhere. Enforcing that needs a validation on the `reports.location` field
 * in Directus.
 */

/**
 * Simplified outline of the contiguous United States, as [lng, lat] vertices.
 *
 * A bounding box is not good enough here: the box around the lower 48 also
 * swallows southern Canada (Toronto, Vancouver) and the Mexican border cities,
 * so a report from Toronto would have counted as domestic. This traces the real
 * border coarsely — good to a few kilometres, which is all a coverage check
 * needs.
 */
const CONTIGUOUS_US = [
	// Northern border, west → east
	[-124.8, 48.4],
	[-123.0, 49.0],
	[-95.2, 49.0],
	[-95.2, 49.4], // Northwest Angle, Minnesota
	[-94.6, 48.7],
	[-89.5, 48.0],
	[-84.6, 46.5], // Sault Ste. Marie
	[-82.5, 45.3],
	[-82.4, 43.0],
	[-79.0, 43.3], // Niágara
	[-76.8, 43.6],
	[-74.7, 45.0], // Nueva York – Quebec
	[-71.5, 45.0],
	[-69.2, 47.5], // norte de Maine
	[-67.8, 47.1],
	[-67.0, 45.2],
	// Costa atlántica, norte → sur
	[-66.9, 44.8],
	[-70.0, 41.5],
	[-74.0, 40.5],
	[-75.5, 38.0],
	[-76.0, 36.0],
	[-78.0, 33.8],
	[-81.0, 31.0],
	[-80.0, 25.0], // Florida
	[-81.5, 24.4], // Cayo Hueso
	// Golfo de México
	[-84.0, 29.5],
	[-88.0, 30.0],
	[-90.0, 29.0],
	[-94.0, 29.5],
	[-97.2, 25.9], // extremo sur de Texas
	// Frontera con México, este → oeste
	[-99.0, 26.4],
	[-101.0, 29.5],
	[-103.0, 29.0],
	[-104.5, 29.6],
	[-106.5, 31.8],
	[-111.0, 31.3],
	[-114.8, 32.5],
	[-117.1, 32.53], // San Diego / Tijuana
	// Costa del Pacífico, sur → norte
	[-120.5, 34.5],
	[-122.0, 37.0],
	[-124.2, 40.4],
	[-124.6, 46.0],
];

/**
 * Outlying territory, still fine as boxes: they are islands with no land
 * border, so nothing foreign creeps in.
 * Format: [southLat, westLng, northLat, eastLng]
 */
const US_BOXES = [
	// Alaska (continente + panhandle)
	[51.0, -172.5, 71.6, -129.9],
	// Alaska, Aleutianas al otro lado del antimeridiano
	[51.0, 172.0, 53.5, 180.0],
	// Hawái
	[18.8, -160.3, 22.3, -154.7],
	// Puerto Rico + Islas Vírgenes
	[17.6, -67.4, 18.6, -64.5],
];

/**
 * Generous rectangle used to fence the map viewport. It has to be a single box
 * (Leaflet takes one), so it is looser than US_BOXES: it keeps you around the
 * country instead of letting you drift to another continent.
 */
export const US_MAP_BOUNDS = [
	[15.0, -180.0],
	[72.5, -63.0],
];

/** Centre used when we have no position at all (geographic centre of the US) */
export const US_DEFAULT_CENTER = [39.83, -98.58];

/**
 * Is this coordinate on US territory?
 * @param {number} lat
 * @param {number} lng
 * @returns {boolean}
 */
export function isInUS(lat, lng) {
	if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;

	const inBox = US_BOXES.some(
		([south, west, north, east]) =>
			lat >= south && lat <= north && lng >= west && lng <= east,
	);

	return inBox || pointInPolygon(lng, lat, CONTIGUOUS_US);
}

/**
 * Ray casting: count how many polygon edges a ray to the right crosses.
 * Odd means inside.
 *
 * @param {number} x - longitude
 * @param {number} y - latitude
 * @param {Array<[number, number]>} polygon - [lng, lat] vertices
 * @returns {boolean}
 */
function pointInPolygon(x, y, polygon) {
	let inside = false;

	for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
		const [xi, yi] = polygon[i];
		const [xj, yj] = polygon[j];

		const crosses =
			yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;

		if (crosses) inside = !inside;
	}

	return inside;
}

/**
 * Same check for a report record (GeoJSON coordinates are [lng, lat]).
 * @param {object} report
 * @returns {boolean}
 */
export function isReportInUS(report) {
	const coords = report?.location?.coordinates;
	if (!Array.isArray(coords) || coords.length < 2) return false;
	return isInUS(coords[1], coords[0]);
}

/**
 * Keep only reports inside the country.
 * @param {Array} reports
 * @returns {Array}
 */
export function filterInUS(reports) {
	if (!Array.isArray(reports)) return [];
	return reports.filter(isReportInUS);
}
